import { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { connectWallet, disconnectWallet } from '../walletService';
import { trackWalletConnect, trackWalletDisconnect } from '../analytics';
import { formatAddress } from '../addressUtils';
import { subscribeToAlias } from '../aliasService';
import { getStoredEmailUserEmail, clearStoredEmailUserEmail } from '../emailAuthService';

/**
 * Custom hook for managing authentication state and handlers
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userOwnAlias, setUserOwnAlias] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Monitor Firebase Auth state and sync with localStorage
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const savedUser = localStorage.getItem('coincommons_user');

      if (firebaseUser) {
        // User is signed in with Firebase
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);

            // Migrate old lastSignIn field to lastSeenAt for backward compatibility
            let dataMigrated = false;
            if (userData.profile && userData.profile.lastSignIn && !userData.profile.lastSeenAt) {
              userData.profile.lastSeenAt = userData.profile.lastSignIn;
              delete userData.profile.lastSignIn;
              dataMigrated = true;
            }

            // Migrate: add uid field for wallet users if missing
            if (!userData.uid && userData.address) {
              userData.uid = userData.address.toLowerCase();
              dataMigrated = true;
            }

            // Check if Firebase Auth session matches localStorage
            // userData.uid is unified: wallet address for wallet users, e_xxx for email users
            const expectedId = userData.uid;

            if (firebaseUser.uid === expectedId) {
              setUser(userData);
              setUserProfile(userData.profile);

              if (dataMigrated) {
                localStorage.setItem('coincommons_user', JSON.stringify(userData));
              }

              if (!userData.profile || !userData.profile.registeredAt) {
                try {
                  const { getUserProfile } = await import('../walletService');
                  const freshProfile = await getUserProfile(firebaseUser.uid);
                  setUserProfile(freshProfile);

                  const serializableProfile = freshProfile ? {
                    ...freshProfile,
                    registeredAt: freshProfile.registeredAt?.toDate?.() || freshProfile.registeredAt,
                    lastSeenAt: freshProfile.lastSeenAt?.toDate?.() || freshProfile.lastSeenAt
                  } : null;

                  const updatedUserData = { ...userData, profile: serializableProfile };
                  localStorage.setItem('coincommons_user', JSON.stringify(updatedUserData));
                } catch (error) {
                  console.error('Error fetching fresh profile:', error);
                }
              }
              setAuthInitialized(true);
            } else {
              console.warn('Firebase Auth UID does not match expected ID', {
                firebaseUID: firebaseUser.uid,
                expectedId
              });
              localStorage.removeItem('coincommons_user');
              setUser(null);
              setUserProfile(null);
              setAuthInitialized(true);
            }
          } catch (error) {
            console.error('Error loading saved user:', error);
            localStorage.removeItem('coincommons_user');
            setAuthInitialized(true);
          }
        } else {
          // Firebase user exists but no localStorage - could be email sign-in completing
          // Check if this is an email user (either by email or by uid starting with e_)
          const isEmailUser = firebaseUser.uid.startsWith('e_');

          if (isEmailUser) {
            // Email user with custom token (uid is already e_<originalUid>)
            const uid = firebaseUser.uid;
            const email = getStoredEmailUserEmail() || firebaseUser.email || null;

            try {
              // Fetch user profile from Firestore (includes username)
              const { doc, getDoc, setDoc, serverTimestamp } = await import('firebase/firestore');
              const addressRef = doc(db, 'addresses', uid);

              // Update lastSeenAt
              await setDoc(addressRef, {
                lastSeenAt: serverTimestamp()
              }, { merge: true });

              // Fetch updated profile (includes username from Cloud Function)
              const updatedDoc = await getDoc(addressRef);
              const docData = updatedDoc.exists() ? updatedDoc.data() : {};
              const profile = {
                registeredAt: docData.registeredAt,
                lastSeenAt: docData.lastSeenAt
              };

              // Get username from Firestore (generated by Cloud Function)
              const username = docData.username || null;
              const displayName = docData.displayName || null;

              const userData = {
                uid, // same as firebaseUser.uid
                email,
                displayName,
                username,
                authMethod: 'email',
                address: null,
                compositeId: uid,
                profile
              };

              setUser(userData);
              setUserProfile(profile);
              localStorage.setItem('coincommons_user', JSON.stringify(userData));
            } catch (error) {
              console.error('Error fetching email user profile:', error);

              const userData = {
                uid,
                email: email,
                displayName: null,
                username: null,
                authMethod: 'email',
                address: null,
                compositeId: uid
              };
              setUser(userData);
              setUserProfile(null);
              localStorage.setItem('coincommons_user', JSON.stringify(userData));
            }

            setAuthInitialized(true);
          } else {
            setAuthInitialized(true);
          }
        }
      } else {
        // No Firebase user
        if (savedUser) {
          if (!authInitialized) {
            setTimeout(() => {
              if (!auth.currentUser && localStorage.getItem('coincommons_user')) {
                console.warn('No Firebase Auth session found after initialization. Clearing session.');
                localStorage.removeItem('coincommons_user');
                setUser(null);
                setUserProfile(null);
              }
            }, 1000);
          } else {
            console.warn('Firebase Auth session ended. Clearing local session.');
            localStorage.removeItem('coincommons_user');
            setUser(null);
            setUserProfile(null);
          }
        }
        setAuthInitialized(true);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to user's own alias (only for wallet users)
  useEffect(() => {
    if (!user || !user.address) {
      setUserOwnAlias(null);
      return;
    }

    // Subscribe to alias for user's own address
    // subscribeToAlias(userAddress, targetAddress, callback, targetChain)
    // userAddress is deprecated (uses auth.currentUser.uid internally)
    const unsubscribe = subscribeToAlias(user.address, user.address, (alias) => {
      setUserOwnAlias(alias);
    });

    return () => unsubscribe();
  }, [user]);

  const handleLogin = async () => {
    try {
      await connectWallet();
    } catch (error) {
      console.error('Login error:', error);
      // Silently handle wallet connection errors to prevent app crash
      // Common errors: user rejected, MetaMask locked, etc.
      // Error details are already logged, no need to rethrow
    }
  };

  const handleLogout = async () => {
    const isEmailUser = user?.authMethod === 'email';

    try {
      if (isEmailUser) {
        // For email users, sign out of Firebase and clear stored email
        await signOut(auth);
        clearStoredEmailUserEmail();
      } else {
        // For wallet users, disconnect the wallet
        await disconnectWallet();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    trackWalletDisconnect();
    setUser(null);
    setUserProfile(null);
    localStorage.removeItem('coincommons_user');
  };

  const handleWalletConnectSuccess = (result) => {
    const { address, chainId, compositeId, chain, profile } = result;
    const lowerAddress = address.toLowerCase();

    const userData = {
      uid: lowerAddress, // Unified uid field: wallet address for wallet users
      address: lowerAddress,
      displayAddress: formatAddress(address),
      chainId,
      compositeId,
      profile
    };

    setUser(userData);
    setUserProfile(profile);
    localStorage.setItem('coincommons_user', JSON.stringify(userData));

    // Use chain from result directly, fallback to 'ethereum'
    trackWalletConnect(result.method || 'unknown', chain || 'ethereum');
  };

  return {
    user,
    userProfile,
    userOwnAlias,
    authInitialized,
    handleLogin,
    handleLogout,
    handleWalletConnectSuccess,
    setUser,
    setUserProfile
  };
};
